/**
 * Load + decode an avatar URL once, then reuse for every Avatar instance.
 * Revealing only after decode avoids progressive top→bottom paint.
 */
const avatarReady = new Map<string, Promise<boolean>>();

export function whenAvatarReady(url: string): Promise<boolean> {
  const existing = avatarReady.get(url);
  if (existing) return existing;

  const promise = new Promise<boolean>((resolve) => {
    const img = new Image();
    img.decoding = 'async';

    const finish = (ok: boolean) => {
      const settle = async () => {
        if (ok && typeof img.decode === 'function') {
          try {
            await img.decode();
          } catch {
            /* decode can reject on abort; bitmap may still be usable */
          }
        }
        resolve(ok && img.naturalWidth > 0);
      };
      void settle();
    };

    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;

    if (img.complete) {
      finish(img.naturalWidth > 0);
    }
  });

  avatarReady.set(url, promise);
  return promise;
}

/** Warm the cache as soon as settings are known (before many Avatar mounts). */
export function prefetchAvatar(url: string | null | undefined): void {
  const trimmed = url?.trim();
  if (trimmed) void whenAvatarReady(trimmed);
}
